"use client";

import { Container } from "react-bootstrap";
import "./PricingTimelineSection.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdNavigateBefore, MdNavigateNext } from "react-icons/md";

const PricingTimelineSection = ({ pricingTimeline }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const autoPlayRef = useRef(null);

  if (!pricingTimeline) return null;

  const { title, description, features = [] } = pricingTimeline;

  const totalItems = features.length;

  const rotate = useCallback(
    (direction = "next") => {
      if (isAnimating) return;

      setIsAnimating(true);

      setActiveIndex((prev) => {
        if (direction === "next") {
          return (prev + 1) % totalItems;
        }

        return (prev - 1 + totalItems) % totalItems;
      });

      setTimeout(() => {
        setIsAnimating(false);
      }, 400);
    },
    [isAnimating, totalItems],
  );

  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      rotate("next");
    }, 3000);

    return () => clearInterval(autoPlayRef.current);
  }, [rotate]);

  const handleManual = (dir) => {
    clearInterval(autoPlayRef.current);

    rotate(dir);

    autoPlayRef.current = setInterval(() => {
      rotate("next");
    }, 3000);
  };

  const getOrbitalStyle = (index) => {
    const rel = (index - activeIndex + totalItems) % totalItems;

    // CENTER
    if (rel === 0) {
      return {
        opacity: 1,
        transform: "translate(0,0) scale(1)",
        zIndex: 100,
        visibility: "visible",
        filter: "blur(0px)",
      };
    }

    // RIGHT TOP
    if (rel === 1) {
      return {
        transform: "translate(380px,-140px) scale(0.28)",
        opacity: 1,
        zIndex: 50,
        filter: "blur(0px)",
      };
    }

    // RIGHT MID
    if (rel === 2) {
      return {
        transform: "translate(460px,90px) scale(0.2)",
        opacity: 0.6,
        zIndex: 40,
        filter: "blur(1px)",
      };
    }

    // RIGHT BOTTOM
    if (rel === 3) {
      return {
        transform: "translate(390px,290px) scale(0.28)",
        opacity: 1,
        zIndex: 50,
        filter: "blur(0px)",
      };
    }

    // LEFT TOP
    if (rel === totalItems - 1) {
      return {
        transform: "translate(-380px,-140px) scale(0.28)",
        opacity: 1,
        zIndex: 50,
        filter: "blur(0px)",
      };
    }

    // LEFT MID
    if (rel === totalItems - 2) {
      return {
        transform: "translate(-460px,90px) scale(0.2)",
        opacity: 0.6,
        zIndex: 40,
        filter: "blur(1px)",
      };
    }

    // LEFT BOTTOM
    if (rel === totalItems - 3) {
      return {
        transform: "translate(-390px,290px) scale(0.28)",
        opacity: 1,
        zIndex: 50,
        filter: "blur(0px)",
      };
    }

    // HIDDEN
    return {
      opacity: 0,
      transform: "translate(0,200px) scale(0)",
      zIndex: 0,
      visibility: "hidden",
    };
  };

  const renderHighlightedTitle = (titleText = "") => {
  const parts = titleText.split(/(".*?")/g);

  return parts.map((part, index) => {
    if (part.startsWith('"') && part.endsWith('"')) {
      return (
        <span
          key={index}
          className="highlight-font"
        >
          {part.slice(1, -1)}
        </span>
      );
    }

    return part;
  });
};

  return (
    <section className="pricing-timeline-section">
      <Container>
        <div className="pricing-header">
          <h2 className="pricing-title">
             {renderHighlightedTitle(title)}
          </h2>

          <p className="pricing-description">{description}</p>
        </div>

        <div className="swiper-viewport">
          {features.map((item, index) => {
            const style = getOrbitalStyle(index);

            const isActive = index === activeIndex;

            return (
              <div key={index} className="orbital-item" style={style}>
                {!isActive ? (
                  <div className="satellite-card">
                    <img src={item.image?.src || item.image} alt={item.title} />
                  </div>
                ) : (
                  <div className="center-content-card">
                    <div className="card-image-wrapper">
                      <img
                        src={item.image?.src || item.image}
                        alt={item.title}
                      />
                    </div>

                    <h3 className="card-title">{item.title}</h3>

                    <p className="card-desc">{item.description}</p>
                  </div>
                )}
              </div>
            );
          })}

          <div className="swiper-controls">
            <button
              className="control-btn"
              onClick={() => handleManual("prev")}
            >
              <MdNavigateBefore className="control-btn-icon" />
            </button>

            <button
              className="control-btn"
              onClick={() => handleManual("next")}
            >
              <MdNavigateNext className="control-btn-icon"/>
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default PricingTimelineSection;
